import { BulkPublishFlow } from "./Gelato";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, imgUrl, formatUSD } from "@/lib/api";
import { Plus, Trash2, X, Upload } from "lucide-react";

export function Artworks() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/artworks").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this artwork?")) return;
    await api.delete(`/admin/artworks/${id}`);
    load();
  };
  const toggleSold = async (a) => {
    await api.patch(`/admin/artworks/${a.id}`, { is_sold: !a.is_sold });
    load();
  };
  const togglePrints = async (a) => {
    await api.patch(`/admin/artworks/${a.id}`, { prints_only: !a.prints_only });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-serif text-4xl mb-1">Artworks</h1>
          <div className="overline text-white/40">{items.length} pieces</div>
        </div>
        <div className="flex gap-2">
          <BulkPublishFlow onDone={load} />
          <button className="bg-white text-black px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
            data-testid="admin-add-artwork" onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus size={14}/> Add Artwork
          </button>
        </div>
      </div>
      {showForm && <ArtworkForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map(a => (
          <div key={a.id} className="surface p-5 flex gap-4" data-testid={`admin-art-${a.id}`}>
            <div className="w-24 h-24 bg-black/40 flex-shrink-0 overflow-hidden">
              {a.image_urls?.[0] && <img src={imgUrl(fileUrl(a.image_urls[0]), 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl truncate">{a.title}</div>
              <div className="text-xs text-white/50 font-mono-brand">{a.category}</div>
              <div className="text-xs text-white/70 font-mono-brand mt-1">
                {(a.sizes || []).map(s => `${s.size} · ${formatUSD(s.price)}`).join(" | ")}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => toggleSold(a)} data-testid={`toggle-sold-${a.id}`}
                  className={`px-2 py-1 text-[10px] uppercase tracking-widest ${a.is_sold ? "bg-red-900 text-white" : "bg-white/10 text-white/60"}`}>
                  {a.is_sold ? "Sold" : "Available"}
                </button>
                <button onClick={() => togglePrints(a)} data-testid={`toggle-prints-${a.id}`}
                  className={`px-2 py-1 text-[10px] uppercase tracking-widest ${a.prints_only ? "bg-amber-800 text-white" : "bg-white/10 text-white/60"}`}>
                  {a.prints_only ? "Prints Only" : "Originals + Prints"}
                </button>
                <button onClick={() => { setEditing(a); setShowForm(true); }} className="px-2 py-1 text-[10px] uppercase tracking-widest bg-white/10 text-white/70">Edit</button>
                <button onClick={() => del(a.id)} data-testid={`delete-art-${a.id}`} className="px-2 py-1 text-[10px] uppercase tracking-widest bg-white/5 text-red-400 hover:bg-red-900/30">
                  <Trash2 size={12}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ArtworkForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? {
    ...initial,
    sizes: initial.sizes || [],
    image_urls: initial.image_urls || [],
  } : {
    title: "", description: "", category: "Original Paintings", medium: "", year: new Date().getFullYear(),
    image_urls: [], sizes: [{ size: "16x20", price: 300, stock: 1 }],
    is_sold: false, prints_only: false, is_featured: false,
  });
  const [uploading, setUploading] = useState(false);
  const [catOptions, setCatOptions] = useState([]);
  useEffect(() => { api.get("/categories").then(r => setCatOptions(r.data.map(c => c.name))).catch(() => {}); }, []);

  const upload = async (file) => {
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setF({ ...f, image_urls: [...f.image_urls, data.path] });
    } catch (e) {
      console.error("Upload failed", e);
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const uploadBulk = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const form = new FormData();
    Array.from(files).forEach(file => form.append("files", file));
    try {
      const { data } = await api.post("/admin/upload-bulk", form, { headers: { "Content-Type": "multipart/form-data" } });
      const newPaths = data.results.filter(r => r.ok).map(r => r.path);
      setF(prev => ({ ...prev, image_urls: [...prev.image_urls, ...newPaths] }));
      const failed = data.results.filter(r => !r.ok).length;
      if (failed > 0) toast.error(`${failed} file(s) failed to upload`);
      if (newPaths.length > 0) toast.success(`Uploaded ${newPaths.length} image(s)`);
    } catch (e) {
      console.error("Bulk upload failed", e);
      toast.error("Bulk upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = { ...f, year: f.year ? Number(f.year) : null,
        sizes: f.sizes.map(s => ({ ...s, price: Number(s.price), stock: Number(s.stock) })) };
      if (initial) await api.patch(`/admin/artworks/${initial.id}`, payload);
      else await api.post("/admin/artworks", payload);
      toast.success(initial ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const updateSize = (i, key, val) => {
    const sizes = [...f.sizes];
    sizes[i] = { ...sizes[i], [key]: val };
    setF({ ...f, sizes });
  };

  return (
    <div className="surface p-6 mb-8" data-testid="artwork-form">
      <div className="flex justify-between items-center mb-4">
        <div className="font-serif text-2xl">{initial ? "Edit" : "New"} Artwork</div>
        <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18}/></button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <input placeholder="Title" data-testid="form-title" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.title} onChange={e => setF({ ...f, title: e.target.value })}/>
        <select data-testid="form-category" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
          {!catOptions.includes(f.category) && f.category && <option value={f.category}>{f.category}</option>}
          {catOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Medium (e.g. Oil on linen)" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.medium || ""} onChange={e => setF({ ...f, medium: e.target.value })}/>
        <input type="number" placeholder="Year" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.year || ""} onChange={e => setF({ ...f, year: e.target.value })}/>
      </div>
      <textarea placeholder="Description" rows={3} className="w-full mt-4 bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.description} onChange={e => setF({ ...f, description: e.target.value })}/>

      <div className="mt-4">
        <div className="overline text-white/50 mb-2">Images</div>
        <div className="flex gap-3 flex-wrap mb-3">
          {f.image_urls.map((u, i) => (
            <div key={u} className="relative w-24 h-24 bg-black/40">
              <img src={imgUrl(fileUrl(u), 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
              <button onClick={() => setF({ ...f, image_urls: f.image_urls.filter((_, j) => j !== i) })}
                className="absolute -top-2 -right-2 bg-red-900 text-white rounded-full w-5 h-5 text-xs">×</button>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer bg-white/10 px-3 py-2 hover:bg-white/20">
          <Upload size={14}/> {uploading ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" className="hidden" data-testid="art-image-upload"
            onChange={e => e.target.files?.[0] && upload(e.target.files[0])}/>
        </label>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer bg-white/10 px-3 py-2 hover:bg-white/20 ml-2">
          <Upload size={14}/> Bulk upload
          <input type="file" accept="image/*" multiple className="hidden" data-testid="art-bulk-upload"
            onChange={e => e.target.files?.length && uploadBulk(e.target.files)}/>
        </label>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="overline text-white/50">Sizes & Prices</div>
          <button onClick={() => setF({ ...f, sizes: [...f.sizes, { size: "", price: 0, stock: 1 }] })} className="text-xs text-white/70 hover:text-white flex items-center gap-1">
            <Plus size={12}/> Add size
          </button>
        </div>
        <div className="space-y-2">
          {f.sizes.map((s, i) => (
            <div key={`size-row-${i}`} className="grid grid-cols-4 gap-2">
              <input placeholder="Size (e.g. 16x20)" className="bg-black/40 border border-white/10 px-3 py-2 text-sm col-span-2" value={s.size} onChange={e => updateSize(i, "size", e.target.value)}/>
              <input type="number" placeholder="Price USD" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={s.price} onChange={e => updateSize(i, "price", e.target.value)}/>
              <div className="flex gap-2">
                <input type="number" placeholder="Stock" className="bg-black/40 border border-white/10 px-3 py-2 text-sm flex-1" value={s.stock} onChange={e => updateSize(i, "stock", e.target.value)}/>
                <button onClick={() => setF({ ...f, sizes: f.sizes.filter((_, j) => j !== i) })} className="text-red-400 px-2"><X size={14}/></button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-white/40 mt-2">Stock -1 = unlimited (for prints).</p>
      </div>

      <div className="flex gap-6 mt-6">
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.is_featured} onChange={e => setF({ ...f, is_featured: e.target.checked })}/> Featured</label>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.is_sold} onChange={e => setF({ ...f, is_sold: e.target.checked })}/> Sold</label>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.prints_only} onChange={e => setF({ ...f, prints_only: e.target.checked })}/> Prints Only</label>
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={save} data-testid="form-save" className="bg-white text-black px-6 py-2 text-xs uppercase tracking-widest">Save</button>
        <button onClick={onClose} className="border border-white/20 px-6 py-2 text-xs uppercase tracking-widest text-white/70">Cancel</button>
      </div>
    </div>
  );
}

